/**
 * Ce que l'écran « Reprise d'ouverture 2026 » lit et écrit en base.
 *
 * ## Le schéma `compta`
 *
 * Le noyau comptable vit dans le schéma `compta` (VALIDÉ ÉCLOSION,
 * 26/09/2026), que l'API ne sert qu'une fois ajouté aux « Exposed schemas »
 * du projet Supabase — un réglage, pas une migration. Tant qu'il ne l'est
 * pas, PostgREST répond `PGRST106` ; l'écran le dit en clair plutôt que
 * d'afficher un message technique.
 *
 * ## Les écritures passent par les gestes
 *
 * Chaque écriture est un appel à une fonction de la base
 * (`saisir_ligne_de_reprise`, `valider_nc_de_transition`,
 * `valider_reprise`...) : c'est là que vivent les contrôles, l'auteur, la date
 * et l'historique. Seule exception : la création d'un tiers, qui est un fait
 * opérationnel (`public.tiers`, réservé aux admins par RLS), pas une écriture
 * comptable.
 *
 * ## Les formes sont déclarées à la main
 *
 * Comme `grandLivre/controle.supabase.ts` : `database.types.ts` ne connaît pas
 * le schéma `compta`, et la migration fait référence.
 */

import { db } from '../../payments/db';
import { enClair } from './erreur';
import type {
  CategorieNc,
  CompteDeBilan,
  DecisionNc,
  EcartClient,
  EtatLigne,
  Exercice,
  LigneDeReprise,
  LigneDeSynthese,
  ModeDeSaisie,
  NoteDeTransition,
  Origine,
  Reprise,
  Rubrique,
  Saisie,
  Sens,
  StatutReprise,
  Traitement,
} from './types';

const compta = () => db.schema('compta');

function echec(e: unknown, defaut?: string): never {
  throw new Error(enClair(e, defaut));
}

/* PostgREST rend un `bigint` en nombre JSON ; les centimes d'Éclosion restent
   très en deçà de 2^53. */
function n(v: unknown): number {
  return v === null || v === undefined ? 0 : Number(v);
}
function nn(v: unknown): number | null {
  return v === null || v === undefined ? null : Number(v);
}
function s(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}

// ---------------------------------------------------------------------------
//  Lectures
// ---------------------------------------------------------------------------

/** Le premier exercice tenu dans CDV, et sa reprise si elle est ouverte. */
export async function lireLaReprise(): Promise<{ exercice: Exercice | null; reprise: Reprise | null }> {
  const ex = await compta()
    .from('exercices')
    .select('id, annee, date_debut')
    .order('annee', { ascending: true })
    .limit(1);
  if (ex.error) echec(ex.error, 'Exercices illisibles');
  const e = (ex.data ?? [])[0] as { id: string; annee: number; date_debut: string } | undefined;
  if (!e) return { exercice: null, reprise: null };
  const exercice: Exercice = { id: e.id, annee: e.annee, dateDebut: e.date_debut };

  const rep = await compta()
    .from('reprises')
    .select('id, exercice_id, date_arrete, statut, validee_le, comptabilisee_le, ecriture_id')
    .eq('exercice_id', e.id)
    .maybeSingle();
  if (rep.error) echec(rep.error, 'Reprise illisible');
  const r = rep.data as Record<string, unknown> | null;
  return {
    exercice,
    reprise: r
      ? {
          id: r.id as string,
          exerciceId: r.exercice_id as string,
          dateArrete: r.date_arrete as string,
          statut: r.statut as StatutReprise,
          valideeLe: s(r.validee_le),
          comptabiliseeLe: s(r.comptabilisee_le),
          ecritureId: s(r.ecriture_id),
        }
      : null,
  };
}

export async function lireLesRubriques(): Promise<Rubrique[]> {
  const { data, error } = await compta()
    .from('rubriques_de_reprise')
    .select('code, libelle, ordre, calculee, saisie, prefixes, sens_habituel, aide')
    .order('ordre');
  if (error) echec(error, 'Rubriques illisibles');
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    code: r.code as string,
    libelle: r.libelle as string,
    ordre: n(r.ordre),
    calculee: Boolean(r.calculee),
    saisie: r.saisie as ModeDeSaisie,
    prefixes: (r.prefixes as string[] | null) ?? null,
    sensHabituel: r.sens_habituel as Sens,
    aide: s(r.aide),
  }));
}

export async function lireLesComptesDeBilan(): Promise<CompteDeBilan[]> {
  const { data, error } = await compta()
    .from('comptes_generaux')
    .select('code, libelle, classe, tiers_obligatoire')
    .gte('classe', 1)
    .lte('classe', 5)
    .eq('is_postable', true)
    .eq('actif', true)
    .order('code');
  if (error) echec(error, 'Plan comptable illisible');
  return ((data ?? []) as Record<string, unknown>[]).map((c) => ({
    code: c.code as string,
    libelle: c.libelle as string,
    classe: n(c.classe),
    tiersObligatoire: Boolean(c.tiers_obligatoire),
  }));
}

export async function lireLaSynthese(repriseId: string): Promise<LigneDeSynthese[]> {
  const { data, error } = await compta()
    .from('v_reprise_synthese')
    .select('*')
    .eq('reprise_id', repriseId);
  if (error) echec(error, 'Synthèse illisible');
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    rubrique: r.rubrique as string,
    rubriqueLibelle: s(r.rubrique_libelle),
    rubriqueOrdre: nn(r.rubrique_ordre),
    calculee: r.calculee === null || r.calculee === undefined ? null : Boolean(r.calculee),
    compteCode: r.compte_code as string,
    compteLibelle: r.compte_libelle as string,
    sens: r.sens as Sens,
    nbLignes: n(r.nb_lignes),
    nbCalculees: n(r.nb_calculees),
    nbSaisies: n(r.nb_saisies),
    nbValidees: n(r.nb_validees),
    nbAValider: n(r.nb_a_valider),
    nbARevoir: n(r.nb_a_revoir),
    nbEnAttente: n(r.nb_en_attente),
    totalCalculeCents: n(r.total_calcule_cents),
    totalValideCents: n(r.total_valide_cents),
    totalRetenuCents: n(r.total_retenu_cents),
    totalEteintCents: n(r.total_eteint_cents),
  }));
}

function versLigne(r: Record<string, unknown>): LigneDeReprise {
  return {
    id: r.id as string,
    rubrique: r.rubrique as string,
    rubriqueLibelle: s(r.rubrique_libelle),
    compteCode: r.compte_code as string,
    compteLibelle: r.compte_libelle as string,
    tiersId: s(r.tiers_id),
    tiersNom: s(r.tiers_nom),
    tiersType: s(r.tiers_type),
    userId: s(r.user_id),
    sens: r.sens as Sens,
    origine: r.origine as Origine,
    libelle: s(r.libelle),
    montantCalculeCents: nn(r.montant_calcule_cents),
    montantValideCents: nn(r.montant_valide_cents),
    montantRetenuCents: nn(r.montant_retenu_cents),
    douteuxCents: n(r.douteux_cents),
    reductionValeurCents: n(r.reduction_valeur_cents),
    eteintCents: n(r.eteint_cents),
    aRevoir: s(r.a_revoir),
    justification: s(r.justification),
    etat: r.etat as EtatLigne,
    valideLe: s(r.valide_le),
  };
}

export interface PageDeLignes {
  lignes: LigneDeReprise[];
  total: number;
}

/**
 * Une page de lignes. `total` est un vrai `count` : PostgREST plafonne une
 * réponse à mille lignes, et un total déduit de la page serait faux sans le
 * dire.
 */
export async function lireLesLignes(
  repriseId: string,
  filtre: { origine?: Origine; etats?: EtatLigne[]; recherche?: string },
  page = 0,
  parPage = 50,
): Promise<PageDeLignes> {
  let q = compta()
    .from('v_reprise_lignes')
    .select('*', { count: 'exact' })
    .eq('reprise_id', repriseId);
  if (filtre.origine) q = q.eq('origine', filtre.origine);
  if (filtre.etats && filtre.etats.length > 0) q = q.in('etat', filtre.etats);
  const texte = filtre.recherche?.trim();
  if (texte) q = q.ilike('tiers_nom', `%${texte}%`);
  const { data, error, count } = await q
    .order('rubrique_ordre')
    .order('compte_code')
    .order('tiers_nom', { nullsFirst: true })
    .range(page * parPage, page * parPage + parPage - 1);
  if (error) echec(error, 'Lignes illisibles');
  return { lignes: ((data ?? []) as Record<string, unknown>[]).map(versLigne), total: count ?? 0 };
}

/**
 * Les notes de transition : le rapport (une ligne par note, proposition ou
 * décision), joint à l'identifiant de sa catégorisation dans la reprise.
 */
export async function lireLesNotesDeTransition(repriseId: string | null): Promise<NoteDeTransition[]> {
  const rap = await compta()
    .from('v_rapport_nc_de_transition')
    .select('*')
    .order('credit_note_date')
    .order('credit_note_number');
  if (rap.error) echec(rap.error, 'Rapport des notes de transition illisible');

  const ids = new Map<string, string>();
  if (repriseId) {
    const cat = await compta().from('reprise_nc').select('id, credit_note_id').eq('reprise_id', repriseId);
    if (cat.error) echec(cat.error, 'Catégorisations illisibles');
    for (const c of (cat.data ?? []) as { id: string; credit_note_id: string }[]) ids.set(c.credit_note_id, c.id);
  }

  return ((rap.data ?? []) as Record<string, unknown>[]).map((r) => ({
    repriseNcId: ids.get(r.credit_note_id as string) ?? null,
    creditNoteId: r.credit_note_id as string,
    numero: r.credit_note_number as string,
    date: r.credit_note_date as string,
    montantCents: n(r.amount_cents),
    factureNumero: s(r.invoice_number),
    factureDate: s(r.invoice_date),
    factureMontantCents: nn(r.invoice_amount_cents),
    payeAvantCents: n(r.paid_before_2026_cents),
    rembourseAvantCents: n(r.refunded_before_2026_cents),
    creanceAvantCents: n(r.opening_receivable_before_adjustment_cents),
    categorie: r.transition_category as CategorieNc,
    categorieProposee: r.proposed_category as CategorieNc,
    validee: Boolean(r.validated),
    ajustementCents: n(r.opening_adjustment_cents),
    detteCents: n(r.opening_liability_cents),
    correctionCents: n(r.current_year_correction_cents),
    raison: s(r.reason),
    blocage: s(r.blocking_reason),
    justification: s(r.justification),
    valideLe: s(r.valide_le),
  }));
}

/**
 * Les tiers clients dont la reprise diffère de l'auxiliaire : familles CDV
 * (grand livre des familles, moins notes d'ouverture et créances éteintes) ;
 * un client hors CDV n'y apparaît pas, sa saisie justifiée fait foi.
 */
export async function lireLesEcartsClients(): Promise<EcartClient[]> {
  const { data, error } = await compta().from('v_controle_reprise_clients').select('*');
  if (error) echec(error, 'Contrôle clients illisible');
  return ((data ?? []) as Record<string, unknown>[])
    .map((l) => ({
      userId: s(l.user_id),
      tiersId: s(l.tiers_id),
      nom: s(l.tiers_nom),
      segment: (l.segment === 'hors_cdv' ? 'hors_cdv' : 'famille') as EcartClient['segment'],
      grandLivreCents: n(l.grand_livre_famille_cents),
      notesCents: n(l.notes_d_ouverture_cents),
      eteintesCents: n(l.creances_eteintes_cents),
      attenduCents: n(l.attendu_cents),
      repriseCents: n(l.reprise_cents),
      ecartCents: n(l.ecart_cents),
    }))
    .sort((a, b) => Math.abs(b.ecartCents) - Math.abs(a.ecartCents));
}

export interface EcritureDOuverture {
  id: string;
  numero: number | null;
  dateEcriture: string;
  totalCents: number;
  nbLignes: number;
}

export async function lireLEcritureDOuverture(ecritureId: string): Promise<EcritureDOuverture | null> {
  const e = await compta().from('ecritures').select('id, numero, date_ecriture').eq('id', ecritureId).maybeSingle();
  if (e.error) echec(e.error, 'Écriture d’ouverture illisible');
  if (!e.data) return null;
  const l = await compta().from('lignes_ecriture').select('debit_cents').eq('ecriture_id', ecritureId);
  if (l.error) echec(l.error, 'Lignes de l’écriture illisibles');
  const lignes = (l.data ?? []) as { debit_cents: unknown }[];
  const d = e.data as { id: string; numero: number | null; date_ecriture: string };
  return {
    id: d.id,
    numero: d.numero,
    dateEcriture: d.date_ecriture,
    totalCents: lignes.reduce((t, x) => t + n(x.debit_cents), 0),
    nbLignes: lignes.length,
  };
}

// ---------------------------------------------------------------------------
//  Les tiers
// ---------------------------------------------------------------------------

export interface TiersTrouve {
  id: string;
  nom: string;
  typeCode: string;
}

export interface TypeDeTiers {
  code: string;
  libelle: string;
}

/**
 * Les tiers d'un nom, hors familles : l'ouverture ne saisit pas de créance
 * famille. `horsCdv` écarte aussi tout tiers relié à un compte CDV — la
 * rubrique clients n'admet que les clients hors CDV (la base le refuse de
 * toute façon : `[reprise-client-cdv]`).
 */
export async function chercherDesTiers(texte: string, horsCdv = false): Promise<TiersTrouve[]> {
  const t = texte.trim();
  if (t.length < 2) return [];
  let q = db
    .from('tiers')
    .select('id, nom, type_code')
    .neq('type_code', 'famille')
    .eq('actif', true)
    .ilike('nom', `%${t}%`);
  if (horsCdv) q = q.is('user_id', null);
  const { data, error } = await q
    .order('nom')
    .limit(20);
  if (error) echec(error, 'Recherche de tiers impossible');
  return ((data ?? []) as { id: string; nom: string; type_code: string }[]).map((x) => ({
    id: x.id, nom: x.nom, typeCode: x.type_code,
  }));
}

export async function lireUnTiers(id: string): Promise<TiersTrouve | null> {
  const { data, error } = await db.from('tiers').select('id, nom, type_code').eq('id', id).maybeSingle();
  if (error) echec(error, 'Tiers illisible');
  const x = data as { id: string; nom: string; type_code: string } | null;
  return x ? { id: x.id, nom: x.nom, typeCode: x.type_code } : null;
}

export async function lireLesTypesDeTiers(): Promise<TypeDeTiers[]> {
  const { data, error } = await db
    .from('tiers_types')
    .select('code, libelle')
    .eq('actif', true)
    .neq('code', 'famille')
    .order('ordre');
  if (error) echec(error, 'Types de tiers illisibles');
  return (data ?? []) as TypeDeTiers[];
}

export async function creerUnTiers(typeCode: string, nom: string): Promise<TiersTrouve> {
  const { data: session } = await db.auth.getUser();
  const { data, error } = await db
    .from('tiers')
    .insert({ type_code: typeCode, nom: nom.trim(), created_by: session.user?.id ?? null })
    .select('id, nom, type_code')
    .single();
  if (error) echec(error, 'Création du tiers impossible');
  const x = data as { id: string; nom: string; type_code: string };
  return { id: x.id, nom: x.nom, typeCode: x.type_code };
}

// ---------------------------------------------------------------------------
//  Les gestes
// ---------------------------------------------------------------------------

async function geste<T>(nom: string, params: Record<string, unknown>): Promise<T> {
  const { data, error } = await compta().rpc(nom, params);
  if (error) echec(error);
  return data as T;
}

export const ouvrirLaReprise = (annee: number) => geste<string>('ouvrir_reprise', { p_annee: annee });

export const proposerLaRepriseClients = (repriseId: string) =>
  geste<unknown>('proposer_la_reprise_clients', { p_reprise_id: repriseId });

export const validerLesLignesCalculees = (repriseId: string) =>
  geste<number>('valider_lignes_calculees', { p_reprise_id: repriseId });

export const validerUneLigne = (ligneId: string, montantCents: number | null, justification: string | null) =>
  geste<void>('valider_ligne_de_reprise', {
    p_ligne_id: ligneId,
    p_montant_cents: montantCents,
    p_justification: justification && justification.trim() !== '' ? justification.trim() : null,
  });

export const saisirUneLigne = (repriseId: string, saisie: Saisie, ligneId: string | null = null) =>
  geste<string>('saisir_ligne_de_reprise', {
    p_reprise_id: repriseId,
    p_rubrique: saisie.rubrique,
    p_compte_code: saisie.compteCode,
    p_sens: saisie.sens,
    p_montant_cents: saisie.montantCents,
    p_justification: saisie.justification,
    p_tiers_id: saisie.tiersId,
    p_libelle: saisie.libelle.trim() === '' ? null : saisie.libelle.trim(),
    p_ligne_id: ligneId,
  });

/**
 * Valider une créance client (400000) et décider ce qu'il en advient à
 * l'ouverture : part douteuse (407000) et sa réduction de valeur (409000),
 * part éteinte (hors ouverture). Trois zéros : une créance ordinaire.
 */
export const validerUneCreance = (ligneId: string, t: Traitement) =>
  geste<void>('valider_creance_d_ouverture', {
    p_ligne_id: ligneId,
    p_montant_cents: t.montantCents,
    p_douteux_cents: t.douteuxCents,
    p_reduction_cents: t.reductionValeurCents,
    p_eteint_cents: t.eteintCents,
    p_justification: t.justification.trim() === '' ? null : t.justification.trim(),
  });

export const retirerUneLigne = (ligneId: string, motif: string) =>
  geste<void>('retirer_ligne_de_reprise', { p_ligne_id: ligneId, p_motif: motif });

export const validerUneNote = (repriseNcId: string, d: DecisionNc) =>
  geste<void>('valider_nc_de_transition', {
    p_reprise_nc_id: repriseNcId,
    p_categorie: d.categorie,
    p_ajustement: d.ajustementCents,
    p_dette: d.detteCents,
    p_correction: d.correctionCents,
    p_justification: d.justification.trim() === '' ? null : d.justification.trim(),
  });

export const validerLaReprise = (repriseId: string) => geste<void>('valider_reprise', { p_reprise_id: repriseId });

export const devaliderLaReprise = (repriseId: string, motif: string) =>
  geste<void>('devalider_reprise', { p_reprise_id: repriseId, p_motif: motif });

export const comptabiliserLaReprise = (repriseId: string) =>
  geste<string>('comptabiliser_reprise', { p_reprise_id: repriseId });
